import { useState, useEffect, useCallback } from 'react';

/**
 * Deteksi apakah app sudah berjalan sebagai PWA (standalone).
 */
function isRunningAsStandalone() {
	return (
		window.matchMedia('(display-mode: standalone)').matches ||
		window.navigator.standalone === true
	);
}

function isMobileDevice() {
	return /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(
		navigator.userAgent
	);
}

/**
 * Hook untuk kontrol install PWA.
 *
 * - Button selalu tampil kecuali sudah running sebagai PWA
 * - Browser yang support beforeinstallprompt → trigger native prompt
 * - Browser yang tidak support (iOS, dll) → showGuide = true, tampilkan instruksi manual
 *
 * @returns {{
 *   showInstall: boolean,
 *   install: () => void,
 *   showGuide: boolean,
 *   closeGuide: () => void,
 *   isIOS: boolean,
 * }}
 */
const DISMISS_KEY = 'cadira_pwa_dismiss_until';
const INSTALLED_KEY = 'cadira_pwa_installed';
const DISMISS_DURATION = 48 * 60 * 60 * 1000; // 48 jam

function isDismissed() {
	const until = localStorage.getItem(DISMISS_KEY);
	if (!until) return false;
	return Date.now() < Number(until);
}

export function useInstallPWA() {
	const [deferredPrompt, setDeferredPrompt] = useState(null);
	const [isStandalone, setIsStandalone] = useState(isRunningAsStandalone);
	const [promptChecked, setPromptChecked] = useState(false);
	const [showGuide, setShowGuide] = useState(false);
	const [userInstalled, setUserInstalled] = useState(
		() => localStorage.getItem(INSTALLED_KEY) === 'true'
	);
	const [bannerDismissed, setBannerDismissed] = useState(isDismissed);

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
	const isAndroid = /Android/i.test(navigator.userAgent);

	useEffect(() => {
		const handler = (e) => {
			e.preventDefault();
			setDeferredPrompt(e);
			setPromptChecked(true);
		};

		window.addEventListener('beforeinstallprompt', handler);

		window.addEventListener('appinstalled', () => {
			setDeferredPrompt(null);
			setIsStandalone(true);
			localStorage.setItem(INSTALLED_KEY, 'true');
			setUserInstalled(true);
		});

		// Android: beforeinstallprompt fire cepat setelah page load.
		// Kalau setelah 3 detik belum fire, berarti PWA sudah terinstall.
		if (isAndroid) {
			const timeout = setTimeout(() => {
				setPromptChecked(true);
			}, 3000);
			return () => {
				clearTimeout(timeout);
				window.removeEventListener('beforeinstallprompt', handler);
			};
		}

		// Listen perubahan display-mode (misal user install dari address bar)
		const mql = window.matchMedia('(display-mode: standalone)');
		const onDisplayChange = (e) => setIsStandalone(e.matches);
		mql.addEventListener('change', onDisplayChange);

		return () => {
			window.removeEventListener('beforeinstallprompt', handler);
			mql.removeEventListener('change', onDisplayChange);
		};
	}, []);

	const install = useCallback(() => {
		if (deferredPrompt) {
			// Android: langsung trigger native prompt
			deferredPrompt.prompt();
			deferredPrompt.userChoice.then(() => setDeferredPrompt(null));
		} else if (isIOS) {
			// iOS: tidak support native prompt → tampilkan guide
			setShowGuide(true);
		}
	}, [deferredPrompt, isIOS]);

	const closeGuide = useCallback(() => {
		setShowGuide(false);
		// iOS: user sudah lihat guide → anggap sudah install
		localStorage.setItem(INSTALLED_KEY, 'true');
		setUserInstalled(true);
	}, []);

	// Android: tampilkan hanya kalau deferredPrompt tersedia (belum install)
	// iOS: tampilkan selalu (tidak ada beforeinstallprompt)
	const canShow = (() => {
		if (isStandalone) return false;
		if (userInstalled) return false;
		if (bannerDismissed) return false;
		if (!isMobileDevice()) return false;
		if (isAndroid) return promptChecked && deferredPrompt !== null;
		return true; // iOS
	})();

	// Dismiss banner selama 48 jam (bukan permanen)
	const dismiss = useCallback(() => {
		const until = Date.now() + DISMISS_DURATION;
		localStorage.setItem(DISMISS_KEY, String(until));
		setBannerDismissed(true);
	}, []);

	return {
		showInstall: canShow,
		install,
		showGuide,
		closeGuide,
		dismiss,
	};
}
